import React, { useEffect, useMemo, useState } from 'react';

import axios, { type AxiosResponse } from 'axios';

import { useDebounce } from '@shared/hooks';

import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { ITEM_CATEGORY_MANAGEMENT } from '@admin/libraries/access_control_catalog.generated';

type CategoryDefault = {
  length?: number | null;
  width?: number | null;
  height?: number | null;
  cuft?: number | null;
  num_movers?: number | null;
  num_movers_with_dolly?: number | null;
  num_movers_if_disassembled?: number | null;
};

type ItemCategory = {
  id: number;
  name: string;
  deleted_at?: string | null;
  move_and_pack_visible?: boolean;
  defaults: Record<string, CategoryDefault>;
  sizing_metric?: {
    enumerations: string[];
    range?: string;
  };
};

type Filters = {
  query?: string;
};

const MP_VISIBLE_OPTIONS = [
  { label: 'M&P Visible', value: true },
  { label: 'M&P Hidden', value: false },
];

const LOAD_ERROR_MESSAGE = 'Unable to load item categories. Please try again.';
const SAVE_ERROR_MESSAGE = 'Unable to save item category. Please try again.';

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeCategory = (category: ItemCategory): ItemCategory => ({
  ...category,
  defaults: category.defaults ?? {},
  sizing_metric: category.sizing_metric
    ? {
        ...category.sizing_metric,
        enumerations: category.sizing_metric.enumerations ?? [],
      }
    : undefined,
});

const parseFiltersFromSearch = (search: string): Filters => {
  const params = new URLSearchParams(search);
  const query = params.get('query');
  return query ? { query } : {};
};

const syncFiltersToUrl = (filters: Filters) => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams();
  if (filters.query) {
    searchParams.set('query', filters.query);
  }
  url.search = searchParams.toString();
  window.history.replaceState({}, '', url.toString());
};

const categoryEnumerationKeys = (category: ItemCategory, first: boolean): string[] => {
  const results: string[] = [];

  if (category.sizing_metric) {
    if (category.sizing_metric.enumerations.length > 0) {
      results.push(...category.sizing_metric.enumerations);
    } else if (category.sizing_metric.range) {
      const range = category.sizing_metric.range.split(',');
      for (let i = parseInt(range[0], 10); i <= parseInt(range[1], 10); i++) {
        results.push(String(i));
      }
    }
  } else {
    results.push('default');
  }

  if (first) {
    return results.slice(0, 1);
  }
  return results.slice(1);
};

const categoryRowspan = (category: ItemCategory): number => {
  let rowspan = 0;
  if (category.sizing_metric) {
    rowspan += categoryEnumerationKeys(category, false).length;
  }
  if (category) {
    rowspan += 1;
  }
  return rowspan;
};

const HighlightedText: React.FC<{ text: string; query?: string }> = ({ text, query }) => {
  if (!query) {
    return <>{text}</>;
  }

  const regex = new RegExp(escapeRegExp(query), 'gi');
  const parts = text.split(regex);
  const matches = text.match(regex);

  if (!matches) {
    return <>{text}</>;
  }

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < matches.length && <mark>{matches[index]}</mark>}
        </React.Fragment>
      ))}
    </>
  );
};

const DefaultCells: React.FC<{
  category: ItemCategory;
  enumeration: string;
  editing: boolean;
  onDefaultChange: (enumeration: string, field: keyof CategoryDefault, value: string) => void;
}> = ({ category, enumeration, editing, onDefaultChange }) => {
  const defaults = category.defaults?.[enumeration] ?? {};

  const field = (name: keyof CategoryDefault, placeholder: string) => (
    <td className="text-left">
      <input
        className="form-control"
        name={`category[default][${enumeration}][${name}]`}
        type="number"
        placeholder={placeholder}
        value={defaults[name] ?? ''}
        onChange={(event) => onDefaultChange(enumeration, name, event.target.value)}
        disabled={!editing}
      />
    </td>
  );

  return (
    <>
      {field('length', 'Length (inches)')}
      {field('width', 'Width (inches)')}
      {field('height', 'Height (inches)')}
      {field('cuft', 'Footage (cuft)')}
      {field('num_movers', 'Movers (#)')}
      {field('num_movers_with_dolly', 'Movers with dolly (#)')}
      {field('num_movers_if_disassembled', 'Movers when disassembled (#)')}
    </>
  );
};

const useCategoriesRequest = () => {
  const [request, setRequest] = useState<Promise<AxiosResponse<ItemCategory[]>> | undefined>();

  useEffect(() => {
    setRequest(axios.get<ItemCategory[]>('/item_categories.json'));
  }, []);

  const reload = () => {
    setRequest(axios.get<ItemCategory[]>('/item_categories.json'));
  };

  return { reload, ...useAxiosRequest(request) };
};

export const ItemCategoryManagement: React.FC = () => {
  useTitle('Item Category Management');
  const canUpdateItemCategories = usePermission(
    ITEM_CATEGORY_MANAGEMENT.resource,
    ITEM_CATEGORY_MANAGEMENT.actions.update,
  );
  const [filters, setFilters] = useState<Filters>(() => parseFiltersFromSearch(window.location.search));
  const [queryInput, setQueryInput] = useState(filters.query ?? '');
  const debouncedQuery = useDebounce(queryInput, 200);
  const { loading, response, error, reload } = useCategoriesRequest();
  const [categories, setCategories] = useState<ItemCategory[] | undefined>();
  const [saveError, setSaveError] = useState<string | undefined>();
  const [selectionID, setSelectionID] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (response?.data) {
      setCategories(response.data.map(normalizeCategory));
    }
  }, [response]);

  useEffect(() => {
    const nextFilters = debouncedQuery ? { query: debouncedQuery } : {};
    setFilters(nextFilters);
    syncFiltersToUrl(nextFilters);
  }, [debouncedQuery]);

  const results = useMemo(() => {
    if (!categories) return undefined;
    if (!filters.query) return categories;

    const query = filters.query.toLowerCase();
    return categories.filter((category) => category.name.toLowerCase().includes(query));
  }, [categories, filters.query]);

  const updateCategory = (categoryID: number, updater: (category: ItemCategory) => ItemCategory) => {
    setCategories((current) => current?.map((category) => (category.id === categoryID ? updater(category) : category)));
  };

  const onDefaultChange = (categoryID: number, enumeration: string, field: keyof CategoryDefault, value: string) => {
    updateCategory(categoryID, (category) => ({
      ...category,
      defaults: {
        ...category.defaults,
        [enumeration]: {
          ...category.defaults[enumeration],
          [field]: value === '' ? null : Number(value),
        },
      },
    }));
  };

  const onVisibilityChange = (categoryID: number, value: boolean) => {
    updateCategory(categoryID, (category) => ({
      ...category,
      move_and_pack_visible: value,
    }));
  };

  const save = async (category: ItemCategory) => {
    setSaving(true);
    setSaveError(undefined);

    const payload = {
      id: category.id,
      move_and_pack_visible: category.move_and_pack_visible,
      category_defaults_attributes: {
        defaults: Object.entries(category.defaults).map(([key, value]) => ({
          key,
          value,
        })),
      },
    };

    try {
      await axios.patch(`/item_categories/${category.id}.json`, payload);
      setSelectionID(null);
      reload();
    } catch {
      setSaveError(SAVE_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const loadError = error ? LOAD_ERROR_MESSAGE : undefined;

  return (
    <Panel>
      <Panel.Header>
        <div className="pad-all">
          <input
            type="text"
            className="form-control"
            placeholder="Search"
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
          />
        </div>
      </Panel.Header>
      <Panel.Body>
        {loadError && (
          <div className="alert alert-danger" role="alert">
            {loadError}
          </div>
        )}
        {saveError && (
          <div className="alert alert-danger" role="alert">
            {saveError}
          </div>
        )}
        <div className="table-responsive">
          <table className="table">
            <tbody>
              {results?.map((category) => {
                const editing = selectionID === category.id;
                const firstEnumerations = categoryEnumerationKeys(category, true);
                const remainingEnumerations = categoryEnumerationKeys(category, false);
                const rowspan = categoryRowspan(category);

                return (
                  <React.Fragment key={category.id}>
                    {firstEnumerations.map((enumeration) => (
                      <tr key={`${category.id}-${enumeration}-first`}>
                        <td className="text-left col-sm-1 col-nowrap" rowSpan={rowspan}>
                          {!category.deleted_at ? (
                            <HighlightedText text={category.name} query={filters.query} />
                          ) : (
                            <del>{category.name}</del>
                          )}
                        </td>
                        <td className="text-left col-sm-1 col-nowrap">{enumeration}</td>
                        <DefaultCells
                          category={category}
                          enumeration={enumeration}
                          editing={editing}
                          onDefaultChange={(enumKey, field, value) =>
                            onDefaultChange(category.id, enumKey, field, value)
                          }
                        />
                        <td className="text-right col-zero col-nowrap" rowSpan={rowspan}>
                          <select
                            className="form-control"
                            name="category[move_and_pack_visible]"
                            value={
                              category.move_and_pack_visible === undefined
                                ? ''
                                : category.move_and_pack_visible
                                  ? 'true'
                                  : 'false'
                            }
                            onChange={(event) => onVisibilityChange(category.id, event.target.value === 'true')}
                            disabled={!editing}
                          >
                            <option disabled value="">
                              - M&P Visibility -
                            </option>
                            {MP_VISIBLE_OPTIONS.map((option) => (
                              <option key={option.label} value={String(option.value)}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-right col-zero col-nowrap" rowSpan={rowspan}>
                          {canUpdateItemCategories &&
                            (editing ? (
                              <button
                                className="btn btn-primary"
                                type="button"
                                onClick={() => void save(category)}
                                disabled={saving}
                              >
                                Save
                              </button>
                            ) : (
                              <button
                                className="btn btn-default"
                                type="button"
                                onClick={() => setSelectionID(category.id)}
                              >
                                Manage
                              </button>
                            ))}
                        </td>
                      </tr>
                    ))}
                    {remainingEnumerations.map((enumeration) => (
                      <tr key={`${category.id}-${enumeration}-rest`}>
                        <td className="text-left col-sm-1 col-nowrap">{enumeration}</td>
                        <DefaultCells
                          category={category}
                          enumeration={enumeration}
                          editing={editing}
                          onDefaultChange={(enumKey, field, value) =>
                            onDefaultChange(category.id, enumKey, field, value)
                          }
                        />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {!results && loading && !loadError && <Spinner />}
      </Panel.Body>
    </Panel>
  );
};
